import { ArrowRight } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router";
import { useResellerMqttSourceDetail } from "@/api/reseller/queries";
import { BackButton } from "@/components/navigation/back-button";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyCell } from "@/components/ui/empty-cell";
import { Skeleton } from "@/components/ui/skeleton";
import { isValidLang, ROUTE_SLUGS } from "@/config/routes";
import { displayDate } from "@/lib/empty-value";

function DetailRow({
	label,
	value,
}: {
	label: string;
	value: React.ReactNode;
}) {
	return (
		<div className="flex items-center justify-between py-2 border-b last:border-0">
			<span className="text-sm text-muted-foreground">{label}</span>
			<span className="text-sm font-medium">{value ?? <EmptyCell />}</span>
		</div>
	);
}

function SourceDetailSkeleton() {
	return (
		<div className="flex flex-col gap-4">
			<Skeleton className="h-8 w-48" />
			<div className="grid gap-4 md:grid-cols-2">
				{Array.from({ length: 3 }).map((_, i) => (
					<Card key={`skeleton-card-${i}`}>
						<CardContent className="pt-6 flex flex-col gap-3">
							{Array.from({ length: 4 }).map((_, j) => (
								<Skeleton key={`skeleton-${i}-${j}`} className="h-4 w-full" />
							))}
						</CardContent>
					</Card>
				))}
			</div>
		</div>
	);
}

export function MqttSourceDetailPage() {
	const { sn, lang } = useParams<{ sn: string; lang: string }>();
	const navigate = useNavigate();
	const { t, i18n } = useTranslation();
	const currentLang = lang && isValidLang(lang) ? lang : "pl";
	const locale = i18n.language === "en" ? "en-GB" : "pl-PL";
	const { data, isLoading } = useResellerMqttSourceDetail(sn ?? "");

	if (!sn) {
		return (
			<Navigate
				to={`/${currentLang}/${ROUTE_SLUGS.sources[currentLang]}`}
				replace
			/>
		);
	}

	function goToReadings() {
		navigate(
			`/${currentLang}/${ROUTE_SLUGS.readings[currentLang]}?search=${sn}`,
		);
	}

	return (
		<div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 lg:p-6">
			<div className="flex items-center gap-3">
				<BackButton
					onClick={() =>
						navigate(`/${currentLang}/${ROUTE_SLUGS.sources[currentLang]}`)
					}
				/>
				<div>
					<p className="text-xs text-muted-foreground">
						{t("mqttSourceDetail.pageSubtitle")}
					</p>
					<h1 className="text-lg font-semibold">
						{isLoading ? <Skeleton className="h-6 w-40 inline-block" /> : sn}
					</h1>
				</div>
				<Button
					size="sm"
					variant="ghost"
					className="ml-auto gap-1.5"
					onClick={goToReadings}
				>
					{t("mqttSourceDetail.readings")}
					<ArrowRight className="size-4" />
				</Button>
			</div>

			{isLoading ? (
				<SourceDetailSkeleton />
			) : !data ? (
				<p className="text-muted-foreground">
					{t("mqttSourceDetail.notFound")}
				</p>
			) : (
				<div className="grid gap-4 md:grid-cols-2">
					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-sm font-medium">
								{t("mqttSourceDetail.generalInfo")}
							</CardTitle>
						</CardHeader>
						<CardContent>
							<DetailRow
								label={t("mqttSourceDetail.district")}
								value={
									data.district_name ?? (
										<span className="text-muted-foreground">—</span>
									)
								}
							/>
							<DetailRow
								label={t("mqttSourceDetail.metersCount")}
								value={data.meters_count}
							/>
							<DetailRow
								label={t("mqttSourceDetail.nextReading")}
								value={
									data.next_reading_date
										? new Date(data.next_reading_date).toLocaleDateString(
												locale,
											)
										: null
								}
							/>
							<DetailRow
								label={t("mqttSourceDetail.readingDays")}
								value={data.reading_days?.join(", ")}
							/>
							<DetailRow
								label={t("mqttSourceDetail.createdAt")}
								value={displayDate(data.created_at, locale)}
							/>
							<DetailRow
								label={t("mqttSourceDetail.updatedAt")}
								value={displayDate(data.updated_at, locale)}
							/>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-sm font-medium">
								{t("mqttSourceDetail.communicationStatus")}
							</CardTitle>
						</CardHeader>
						<CardContent>
							<DetailRow
								label={t("mqttSourceDetail.lastCommunication")}
								value={displayDate(data.last_communication, locale)}
							/>
							<DetailRow
								label={t("mqttSourceDetail.counter")}
								value={data.counter}
							/>
							<DetailRow
								label={t("mqttSourceDetail.batteryLevel")}
								value={
									data.battery_level != null ? `${data.battery_level}%` : null
								}
							/>
							<DetailRow
								label={t("mqttSourceDetail.lastReading")}
								value={
									data.last_reading ? (
										<div className="flex items-center gap-2">
											<Badge
												variant={
													data.last_reading.completed ? "success" : "info"
												}
											>
												{data.last_reading.completed
													? t("mqttSourceDetail.completed")
													: t("mqttSourceDetail.inProgress")}
											</Badge>
											<span className="text-xs text-muted-foreground">
												{new Date(
													data.last_reading.reading_date,
												).toLocaleDateString(locale)}
											</span>
										</div>
									) : (
										"—"
									)
								}
							/>
						</CardContent>
					</Card>
				</div>
			)}
		</div>
	);
}
